/*
 * Sticky Add-to-Cart — custom element.
 * Comportamento:
 *  1. IntersectionObserver sull'ATC originale: sticky appare quando ATC esce dal viewport.
 *  2. Click sticky btn → forwarda click sull'ATC originale (preserva Dawn add-to-cart logic).
 *  3. Sincronizzazione swatch / taglia / prezzo: legge il DOM al cambio variante.
 *  4. Body class sticky-atc-active per riposizionare back-to-top e chat widget.
 */

if (!customElements.get('sticky-add-to-cart')) {
  class StickyAddToCart extends HTMLElement {
    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
      } else {
        setTimeout(() => this.init(), 50);
      }
    }

    init() {
      if (!window.matchMedia('(max-width: 749px)').matches) return;

      this.sectionId = this.dataset.sectionId;
      this.swatchEl = this.querySelector('.sticky-atc__swatch');
      this.variantEl = this.querySelector('.sticky-atc__variant');
      this.priceEl = this.querySelector('.sticky-atc__price');
      this.btnEl = this.querySelector('.sticky-atc__btn');

      // Trova il button ATC principale (escludendo form installment Shop Pay / Klarna).
      // Uso getAttribute('id') perché form.id ritorna l'input child con name="id" (named access HTML).
      const allAddBtns = document.querySelectorAll('button[name="add"], .product-form__submit');
      this.originalAtcBtn = null;
      for (const btn of allAddBtns) {
        const parentForm = btn.closest('form');
        const formId = (parentForm && parentForm.getAttribute('id')) || '';
        const isInstallment = typeof formId === 'string' && formId.indexOf('installment') !== -1;
        if (!isInstallment) {
          this.originalAtcBtn = btn;
          this.originalForm = parentForm;
          break;
        }
      }
      if (!this.originalAtcBtn) {
        this.style.display = 'none';
        return;
      }

      this.setupClickForward();
      this.setupVisibilityObserver();
      this.setupVariantSync();
      this.syncFromDom();
    }

    setupClickForward() {
      this.btnEl.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.originalAtcBtn.disabled) return;
        this.originalAtcBtn.click();
        // Feedback micro: pulse class
        this.classList.add('sticky-atc--pulse');
        setTimeout(() => this.classList.remove('sticky-atc--pulse'), 280);
      });
    }

    setupVisibilityObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const rect = entry.boundingClientRect;
            const showSticky = rect.bottom < 0;
            this.applyStickyState(showSticky);
          });
        },
        { threshold: 0 }
      );
      this.observer.observe(this.originalAtcBtn);

      // Stato iniziale: se la pagina e' caricata gia' scrollata, l'IO spara comunque ma
      // iubenda potrebbe non essere ancora nel DOM. Calcolo lo stato sincronamente.
      const initialRect = this.originalAtcBtn.getBoundingClientRect();
      this._stickyActive = initialRect.bottom < 0;
      this.applyStickyState(this._stickyActive);

      // MutationObserver: iubenda viene iniettato in modo asincrono. Quando appare,
      // se la sticky e' gia' attiva, applico immediatamente lo style override.
      this.iubendaWatcher = new MutationObserver(() => {
        if (this._stickyActive) {
          this.adjustIubendaOnly(true);
        }
      });
      this.iubendaWatcher.observe(document.body, { childList: true, subtree: true });
    }

    applyStickyState(showSticky) {
      this._stickyActive = showSticky;
      this.classList.toggle('is-visible', showSticky);
      document.body.classList.toggle('sticky-atc-active', showSticky);
      this.setAttribute('aria-hidden', showSticky ? 'false' : 'true');
      this.adjustIubendaOnly(showSticky);
    }

    adjustIubendaOnly(stickyActive) {
      // Solo iubenda richiede inline style override: il suo CSS interno ha specificity
      // alta che batte la nostra regola body.sticky-atc-active .iubenda-tp-btn.
      // Aggiungo transition inline per match visivo con sienna / chat (CSS gia' transition 0.3s).
      const iubendaEls = document.querySelectorAll('.iubenda-tp-btn, .iubenda-cs-preferences-link');
      iubendaEls.forEach((el) => {
        if (stickyActive) {
          // Setto transition PRIMA del bottom, cosi' il primo cambio anima.
          el.style.setProperty('transition', 'bottom 0.3s ease, margin 0.3s ease', 'important');
          el.style.setProperty('bottom', '80px', 'important');
          el.style.setProperty('margin-top', '0', 'important');
          el.style.setProperty('margin-bottom', '0', 'important');
        } else {
          // Tengo la transition cosi' anche il ritorno e' fluido, poi la rimuovo dopo l'animazione.
          el.style.setProperty('transition', 'bottom 0.3s ease, margin 0.3s ease', 'important');
          el.style.removeProperty('bottom');
          el.style.removeProperty('margin-top');
          el.style.removeProperty('margin-bottom');
        }
      });
    }

    setupVariantSync() {
      // Dawn dispatcha eventi al cambio variante. Ascolto piu' segnali.
      const handler = () => {
        // Aspetta che Dawn aggiorni il DOM (il prezzo cambia con delay)
        setTimeout(() => this.syncFromDom(), 80);
      };

      document.addEventListener('change', (e) => {
        const t = e.target;
        if (!t.matches) return;
        // Dawn 15.4 Gala: name="Color-N"/"Size-N", oltre a fallback Shopify standard
        if (t.matches('input[name="id"], input[name^="options"], select[name^="options"], input[type="radio"][name^="Color-"], input[type="radio"][name^="Size-"], input[type="radio"][name^="Colore-"], input[type="radio"][name^="Taglia-"], input[type="radio"][name^="Farbe-"], input[type="radio"][name^="Größe-"]')) {
          handler();
        }
      });
      // Eventi custom Dawn (variant-selects)
      document.addEventListener('variant:change', handler);
      document.addEventListener('on:variant:change', handler);

      // Click sui label degli swatch (in caso non sparino 'change' event)
      document.addEventListener('click', (e) => {
        if (e.target.closest('label.swatch, .product-form__input label, .variant-selects__radio-label')) {
          handler();
        }
      });
    }

    syncFromDom() {
      // Dawn 15.4 Gala usa name pattern "Color-N" / "Size-N" (N = option index),
      // non "options[Color]"/"options[Size]". Coverage multi-locale via i18n option names.
      const colorInput = document.querySelector(
        'input[type="radio"][name^="Color-"]:checked, ' +
        'input[type="radio"][name^="Colour-"]:checked, ' +
        'input[type="radio"][name^="Colore-"]:checked, ' +
        'input[type="radio"][name^="Farbe-"]:checked, ' +
        'input[type="radio"][name^="カラー-"]:checked, ' +
        'input[type="radio"][name^="色-"]:checked'
      );
      const sizeInput = document.querySelector(
        'input[type="radio"][name^="Size-"]:checked, ' +
        'input[type="radio"][name^="Taglia-"]:checked, ' +
        'input[type="radio"][name^="Größe-"]:checked, ' +
        'input[type="radio"][name^="サイズ-"]:checked'
      );

      const parts = [];
      if (colorInput && colorInput.value) parts.push(colorInput.value);
      if (sizeInput && sizeInput.value) parts.push(sizeInput.value);
      if (this.variantEl) {
        this.variantEl.textContent = parts.length > 0 ? parts.join(' · ') : '';
      }

      // === Swatch: Dawn 15.4 markup REALE (NON l'input dentro la label):
      //   <input type="radio" id="template--...-1-0" name="Color-1" value="Blue" class="swatch-input__input">
      //   <label for="template--...-1-0" title="Blue" class="swatch-input__label">
      //     <span class="swatch" style="--swatch--background: rgb(0 91 211);"></span>
      //   </label>
      // Input e label sono SIBLING, non parent/child. Trovo la label via for=id.
      if (this.swatchEl && colorInput) {
        let label = null;
        if (colorInput.id) {
          // querySelector con id che contiene ":" o caratteri speciali richiede CSS.escape
          try {
            label = document.querySelector('label[for="' + CSS.escape(colorInput.id) + '"]');
          } catch (e) {
            label = document.querySelector('label[for="' + colorInput.id + '"]');
          }
        }
        // Fallback: cerca via title=value (Dawn setta title="Blue" sulla label)
        if (!label && colorInput.value) {
          const labels = document.querySelectorAll('label.swatch-input__label[title]');
          for (const l of labels) {
            if (l.getAttribute('title') === colorInput.value) { label = l; break; }
          }
        }
        const swatchSpan = label && label.querySelector('.swatch');
        if (swatchSpan) {
          // Leggo inline CSS var --swatch--background (Dawn 15.4 la setta inline sullo span)
          const rawColor = swatchSpan.style.getPropertyValue('--swatch--background');
          const rawImage = swatchSpan.style.getPropertyValue('--swatch--background-image');
          if (rawImage && rawImage.trim() && rawImage.trim() !== 'none') {
            this.swatchEl.style.backgroundImage = rawImage.trim();
          } else {
            this.swatchEl.style.backgroundImage = 'none';
          }
          if (rawColor && rawColor.trim()) {
            this.swatchEl.style.backgroundColor = rawColor.trim();
          } else {
            // Fallback: computed background
            const cs = window.getComputedStyle(swatchSpan);
            if (cs.backgroundColor && cs.backgroundColor !== 'rgba(0, 0, 0, 0)' && cs.backgroundColor !== 'transparent') {
              this.swatchEl.style.backgroundColor = cs.backgroundColor;
            }
          }
        }
      }

      // === Prezzo dal DOM live ===
      if (this.priceEl) {
        const priceCandidate = document.querySelector(
          '.product__price [data-shopify-text="price"], ' +
          '.product__price .price__sale .price-item--sale, ' +
          '.product__price .price__regular .price-item--regular, ' +
          '.product__price .price-item, ' +
          '.price__container .price-item--regular, ' +
          '.price-item.price-item--regular'
        );
        if (priceCandidate) {
          this.priceEl.textContent = priceCandidate.textContent.trim();
        }
      }

      // === Sold-out state ===
      const isSoldOut = this.originalAtcBtn.disabled || /sold[\s-]?out/i.test(this.originalAtcBtn.textContent || '');
      this.classList.toggle('is-sold-out', isSoldOut);
      if (isSoldOut) {
        this.btnEl.disabled = true;
        // Riprende il testo dal bottone originale per coerenza i18n
        const origText = this.originalAtcBtn.querySelector('span') || this.originalAtcBtn;
        this.btnEl.querySelector('.sticky-atc__btn-text').textContent = (origText.textContent || 'Sold out').trim();
      } else {
        this.btnEl.disabled = false;
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.iubendaWatcher) this.iubendaWatcher.disconnect();
      document.body.classList.remove('sticky-atc-active');
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
